import React, { useState, useEffect } from 'react';
import { Container, Table, Tabs, Tab, Spinner, Alert, Badge, Form, Row, Col, Button } from 'react-bootstrap';
import api, { API_URL } from '../services/api';
import './WarehouseReceipts.css';
import WRDetailsModal from './WRDetailsModal';

function WarehouseReceipts() {
  const [activeTab, setActiveTab] = useState('open');
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedWR, setSelectedWR] = useState(null);
  const [wrDetails, setWrDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Date input values (what's shown in the inputs)
  const [fromDateInput, setFromDateInput] = useState(() => {
    // Default to 30 days ago
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  });
  
  const [toDateInput, setToDateInput] = useState(() => {
    // Default to today
    return new Date().toISOString().split('T')[0]; // Format as YYYY-MM-DD
  });
  
  // Actual date filter values used for API calls
  const [fromDateFilter, setFromDateFilter] = useState(fromDateInput);
  const [toDateFilter, setToDateFilter] = useState(toDateInput);

  useEffect(() => {
    fetchReceipts();
  }, [activeTab, fromDateFilter, toDateFilter]);

  // Reset dates when switching tabs
  useEffect(() => {
    if (activeTab === 'open') {
      // No need to update filter dates for open tab
      return;
    }
    
    // When switching to complete tab, apply the input dates
    if (activeTab === 'complete') {
      applyDateFilter();
    }
  }, [activeTab]);

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      setError(null);
      let endpoint = activeTab === 'open' ? '/api/warehouse/receipts/open' : '/api/warehouse/receipts/complete';
      
      // Add date parameters for complete receipts
      if (activeTab === 'complete' && fromDateFilter && toDateFilter) {
        endpoint += `?fromDate=${fromDateFilter}&toDate=${toDateFilter}`;
      }
      
      // Add cache-busting parameter
      endpoint += endpoint.includes('?') ? `&_t=${new Date().getTime()}` : `?_t=${new Date().getTime()}`;
      
      console.log('Fetching receipts from:', endpoint);
      const response = await api.get(endpoint);
      console.log('Received response:', response.data);
      setReceipts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching warehouse receipts:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError('Failed to load warehouse receipts. Please try again later.');
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStatusBadge = (isIT, isCleared) => {
    if (isIT) {
      return <Badge bg="warning">IT</Badge>;
    }
    if (isCleared) {
      return <Badge bg="success">Cleared</Badge>;
    }
    return <Badge bg="secondary">Pending</Badge>;
  };

  const renderStatusIcon = (isIT, isCleared) => {
    if (isCleared) {
      return <i className="fas fa-check-circle text-success ms-1" title="Cleared"></i>;
    }
    if (isIT) {
      return <i className="fas fa-warehouse text-warning ms-1" title="In Bond"></i>;
    }
    return null;
  };

  // Filter receipts by search term across all columns
  const filteredReceipts = receipts.filter(receipt => {
    const searchLower = searchTerm.toLowerCase();
    return (
      receipt.idwarehouse?.toString().toLowerCase().includes(searchLower) ||
      receipt.bookingNum?.toLowerCase().includes(searchLower) ||
      receipt.MAWB?.toLowerCase().includes(searchLower) ||
      receipt.HAWB?.toLowerCase().includes(searchLower) ||
      receipt.CustomerRef?.toLowerCase().includes(searchLower) ||
      receipt.recDate?.toLowerCase().includes(searchLower) ||
      receipt.entryDate?.toLowerCase().includes(searchLower) ||
      receipt.shipper?.toLowerCase().includes(searchLower) ||
      receipt.FreightDesc?.toLowerCase().includes(searchLower) ||
      receipt.TotalPieces?.toString().toLowerCase().includes(searchLower) ||
      receipt.TotalWeight?.toString().toLowerCase().includes(searchLower)
    );
  });

  // Handle clicking on a warehouse receipt row
  const handleRowClick = async (receipt) => {
    try {
      setSelectedWR(receipt);
      setLoadingDetails(true);
      setShowModal(true);
      
      const response = await api.get(`/api/warehouse/receipts/${receipt.idwarehouse}/details`);
      console.log('Received WR details:', response.data);
      setWrDetails(response.data);
    } catch (err) {
      console.error('Error fetching WR details:', err);
      // Show error in modal instead of setting global error
      setWrDetails({ error: 'Failed to load warehouse receipt details. Please try again.' });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle input date changes (doesn't trigger API call)
  const handleDateChange = (e, setter) => {
    setter(e.target.value);
  };

  // Apply date filter (this will trigger API call via useEffect)
  const applyDateFilter = () => {
    setFromDateFilter(fromDateInput);
    setToDateFilter(toDateInput);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedWR(null);
    setWrDetails(null);
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <div className="warehouse-receipts-container">
      <h2>Warehouse Receipts</h2>
      
      <div className="mb-3">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
        >
          <Tab eventKey="open" title="Open Receipts" />
          <Tab eventKey="complete" title="Complete Receipts" />
        </Tabs>
      </div>
      
      <div className="d-flex align-items-center justify-content-between mb-3">
        {activeTab === 'complete' && (
          <div className="date-filters d-flex align-items-center">
            <div className="me-3">
              <Form.Group as={Row} className="mb-0 align-items-center">
                <Form.Label column sm={4} className="mb-0">From:</Form.Label>
                <Col sm={8}>
                  <Form.Control
                    type="date"
                    value={fromDateInput}
                    onChange={(e) => handleDateChange(e, setFromDateInput)}
                    size="sm"
                  />
                </Col>
              </Form.Group>
            </div>
            <div className="me-3">
              <Form.Group as={Row} className="mb-0 align-items-center">
                <Form.Label column sm={3} className="mb-0">To:</Form.Label>
                <Col sm={9}>
                  <Form.Control
                    type="date"
                    value={toDateInput}
                    onChange={(e) => handleDateChange(e, setToDateInput)}
                    size="sm"
                  />
                </Col>
              </Form.Group>
            </div>
            <Button 
              variant="primary" 
              size="sm" 
              onClick={applyDateFilter}
            >
              Apply
            </Button>
          </div>
        )}
        
        <div className="search-box-wrapper position-relative ms-auto" style={{ maxWidth: 300, width: '100%' }}>
          <Form.Control
            type="text"
            placeholder="Search receipts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingRight: searchTerm ? 32 : undefined }}
          />
          {searchTerm && (
            <span
              className="search-clear-x"
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#888',
                fontSize: 18,
                zIndex: 2
              }}
              title="Clear search"
            >
              &#10005;
            </span>
          )}
        </div>
      </div>
      
      <div className="table-container">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>WR#</th>
              <th>Booking #</th>
              <th>MAWB</th>
              <th>HAWB</th>
              <th>Customer Ref</th>
              <th>Rec Date</th>
              <th>Entry Date</th>
              <th>Shipper</th>
              <th>Description</th>
              <th>Pcs</th>
              <th>Wt</th>
            </tr>
          </thead>
          <tbody>
            {filteredReceipts && filteredReceipts.length > 0 ? (
              filteredReceipts.map((receipt) => (
                <tr 
                  key={receipt.idFreight} 
                  onClick={() => handleRowClick(receipt)}
                  style={{ cursor: 'pointer' }}
                  className="clickable-row"
                >
                  <td>
                    {receipt.idwarehouse}
                    {renderStatusIcon(receipt.isIT, receipt.isCleared)}
                  </td>
                  <td>{receipt.bookingNum}</td>
                  <td>{receipt.MAWB}</td>
                  <td>{receipt.HAWB}</td>
                  <td>{receipt.CustomerRef}</td>
                  <td>{formatDate(receipt.recDate)}</td>
                  <td>{formatDate(receipt.entryDate)}</td>
                  <td>{receipt.shipper}</td>
                  <td>{receipt.FreightDesc}</td>
                  <td>{receipt.TotalPieces}</td>
                  <td>{receipt.TotalWeight} lbs</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="text-center">
                  No {activeTab} receipts found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Modal for warehouse receipt details */}
      <WRDetailsModal 
        show={showModal} 
        onHide={handleCloseModal} 
        wrDetails={wrDetails}
      />
    </div>
  );
}

export default WarehouseReceipts; 